import React, { useRef, useState } from 'react';
import { C } from '../theme';
import api from '../api/axios';

const INV_ORANGE = '#F5A623';
const INV_DEEP   = '#C17D00';

export default function InvoiceModal({ payment, onClose, canSend = true }) {
  const printRef = useRef(null);
  const [sending, setSending] = useState(null);   // 'wa' | 'email' | null
  const [note,    setNote]    = useState(null);   // { ok, text }

  const send = async (channel) => {
    if (!payment?.id) return;
    setSending(channel);
    setNote(null);
    try {
      const url = channel === 'wa'
        ? `/api/payments/${payment.id}/send-whatsapp`
        : `/api/payments/${payment.id}/send-email`;
      const r = await api.post(url);
      setNote({ ok: true, text: r.data.message });
    } catch (e) {
      setNote({ ok: false, text: e?.response?.data?.detail || 'Send failed.' });
    } finally {
      setSending(null);
      setTimeout(() => setNote(null), 5000);
    }
  };

  if (!payment) return null;

  const isPartial = payment.is_partial || (payment.balance_due > 0.01);
  const isOnline  = payment.payment_mode === 'online';
  const isRenewal = payment.entry_type === 'renewal';

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    const win = window.open('', '_blank', 'width=800,height=900');
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${payment.payment_id}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #111; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,14,4,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '12px' }}>
      <div style={{ background: '#fff', borderRadius: '18px', width: '100%', maxWidth: '680px', maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 30px 90px rgba(0,0,0,0.35)', border: `1px solid ${C.border}` }}>

        {/* Action bar (outside printable area) */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FAFAFA', borderRadius: '18px 18px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>
              {isRenewal ? '🔄 AMC Renewal Receipt' : '💳 Payment Receipt'}
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: '12px', background: '#ECFDF5', color: '#065F46', padding: '2px 8px', borderRadius: '6px', fontWeight: 800 }}>{payment.payment_id}</span>
            <span style={{
              padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800,
              background: isPartial ? '#FEF3C7' : '#D1FAE5',
              color:      isPartial ? '#92400E'  : '#065F46',
            }}>
              {isPartial ? 'PARTIAL PAYMENT' : 'FULL PAYMENT'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={handlePrint} style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: '#F3F4F6', color: C.textMain, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
              🖨️ Print
            </button>
            {canSend && (
              <>
                <button
                  onClick={() => send('wa')}
                  disabled={sending !== null || !payment.client_contact}
                  title={payment.client_contact ? 'Send invoice to client on WhatsApp' : 'Client has no contact number'}
                  style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: payment.client_contact ? 'linear-gradient(135deg,#25D366,#128C7E)' : '#E5E7EB', color: payment.client_contact ? '#fff' : '#9CA3AF', border: 'none', cursor: (sending || !payment.client_contact) ? 'default' : 'pointer', opacity: sending === 'wa' ? 0.7 : 1 }}>
                  {sending === 'wa' ? 'Sending…' : '💬 WhatsApp'}
                </button>
                <button
                  onClick={() => send('email')}
                  disabled={sending !== null || !payment.client_email}
                  title={payment.client_email ? 'Email the receipt to client' : 'Client has no email'}
                  style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: payment.client_email ? `linear-gradient(135deg, ${INV_ORANGE}, ${INV_DEEP})` : '#E5E7EB', color: payment.client_email ? '#fff' : '#9CA3AF', border: 'none', cursor: (sending || !payment.client_email) ? 'default' : 'pointer', opacity: sending === 'email' ? 0.7 : 1 }}>
                  {sending === 'email' ? 'Sending…' : '✉️ Email'}
                </button>
              </>
            )}
            <button onClick={onClose} style={{ padding: '7px 12px', borderRadius: '8px', fontSize: '13px', background: '#F3F4F6', color: C.textSub, border: `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 600 }}>✕</button>
          </div>
        </div>

        {/* Send result banner */}
        {note && (
          <div style={{
            padding: '9px 20px', fontSize: '12px', fontWeight: 600,
            background: note.ok ? '#ECFDF5' : '#FEF2F2',
            color:      note.ok ? '#065F46' : '#991B1B',
            borderBottom: `1px solid ${note.ok ? '#A7F3D0' : '#FECACA'}`,
          }}>
            {note.ok ? '✓ ' : '⚠ '}{note.text}
          </div>
        )}

        {/* ── Printable invoice body ── */}
        <div ref={printRef}>
          <div style={{ padding: '32px 40px', fontFamily: "'Segoe UI', Arial, sans-serif" }}>

            {/* Invoice Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `linear-gradient(135deg, ${INV_ORANGE}, ${INV_DEEP})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🐝</div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#111', letterSpacing: '-0.5px' }}>HIVE ERP</div>
                    <div style={{ fontSize: '10px', color: '#888', fontWeight: 500 }}>Enterprise Resource Planning</div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '22px', fontWeight: 900, color: isRenewal ? '#7C3AED' : INV_ORANGE, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' }}>
                  {isRenewal ? (isPartial ? 'Partial AMC' : 'AMC Receipt') : (isPartial ? 'Partial Receipt' : 'Payment Receipt')}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', fontFamily: 'monospace' }}>{payment.payment_id}</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Date: {payment.created_date || payment.created_at}</div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: '3px', background: isRenewal ? 'linear-gradient(90deg, #7C3AED, #C4B5FD50, transparent)' : `linear-gradient(90deg, ${INV_ORANGE}, ${INV_DEEP}50, transparent)`, borderRadius: '2px', marginBottom: '24px' }} />

            {/* Bill To + Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
              {/* Bill To */}
              <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '16px' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Bill To</div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: '#111', marginBottom: '4px' }}>{payment.client_name}</div>
                <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px', fontFamily: 'monospace', fontWeight: 600 }}>{payment.client_ref}</div>
                {payment.client_contact && <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>📞 {payment.client_contact}</div>}
                {payment.client_email   && <div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>✉️ {payment.client_email}</div>}
                {payment.client_address && <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: '1.4' }}>{payment.client_address}</div>}
              </div>

              {/* Payment Details */}
              <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '16px' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Payment Details</div>
                <Row label="Mode"    value={isOnline ? '🌐 Online' : '💵 Offline'} />
                {payment.payment_method && <Row label="Method" value={payment.payment_method} />}
                <Row label="Installer" value={payment.installer_name || '—'} />
                <Row label="Type"    value={isRenewal ? '🔄 AMC Renewal' : '📦 Product Sale'} />
              </div>
            </div>

            {/* Items Table */}
            <div style={{ marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#111' }}>
                    <th style={{ padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: '#fff', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                    <th style={{ padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: '#fff', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Package</th>
                    <th style={{ padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: '#fff', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '14px', fontSize: '13px', fontWeight: 600, color: '#111' }}>
                      {isRenewal ? 'Annual Maintenance Charge (AMC)' : 'Product / Software Installation'}
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{payment.mapping_id}</div>
                    </td>
                    <td style={{ padding: '14px', verticalAlign: 'top' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>{payment.package_ref}</div>
                      {payment.package_name && <div style={{ fontSize: '11px', color: '#888' }}>{payment.package_name}</div>}
                    </td>
                    <td style={{ padding: '14px', textAlign: 'right', fontSize: '14px', fontWeight: 800, color: '#111' }}>
                      ₹{(payment.total_due || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Payment Summary */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
              <div style={{ width: '300px', background: '#F9FAFB', borderRadius: '10px', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                <SumRow label="Total Due"     val={payment.total_due}   plain />
                <SumRow label="Amount Paid"   val={payment.amount_paid} color="#065F46" />
                {isPartial && <SumRow label="Balance Due" val={payment.balance_due} color="#92400E" />}
                <div style={{ background: isPartial ? '#FEF3C7' : '#D1FAE5', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: isPartial ? '#92400E' : '#065F46', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {isPartial ? '⏳ Partial Payment' : '✓ Paid in Full'}
                  </span>
                  <span style={{ fontSize: '18px', fontWeight: 900, color: isPartial ? '#92400E' : '#065F46' }}>
                    ₹{(payment.amount_paid || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {payment.notes && (
              <div style={{ marginBottom: '20px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', padding: '12px 16px' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Notes</div>
                <div style={{ fontSize: '13px', color: '#78350F', fontStyle: 'italic' }}>{payment.notes}</div>
              </div>
            )}

            {/* Partial notice */}
            {isPartial && (
              <div style={{ background: '#FFF7ED', border: '1px dashed #FDBA74', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '12px', color: '#92400E' }}>
                ⚠ This is a partial payment receipt. Balance of <strong>₹{payment.balance_due.toLocaleString('en-IN')}</strong> remains outstanding and will be collected separately.
              </div>
            )}

            {/* Footer */}
            <div style={{ borderTop: '2px dashed #E5E7EB', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '11px', color: '#AAA' }}>
                Generated by Hive ERP · {payment.created_at}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: INV_ORANGE }} />
                <span style={{ fontSize: '11px', fontWeight: 700, color: INV_DEEP }}>HIVE ERP</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
      <span style={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: 700, color: '#111', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function SumRow({ label, val, color, plain }) {
  return (
    <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E5E7EB' }}>
      <span style={{ fontSize: '12px', color: plain ? '#888' : '#555', fontWeight: plain ? 500 : 600 }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 700, color: color || '#111' }}>₹{(val || 0).toLocaleString('en-IN')}</span>
    </div>
  );
}
